import {
  GeckoCourseJSON,
  GeckoFeatureModelType,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoPlateJSON,
  GeckoVerticalWeldJSON,
} from 'types/index'
import { assetModelPrecision } from 'utils/constants'
import { v4 as uuid4 } from 'uuid'

/**
 * Function to create the Asset Model JSON for a GeckoPlate, to be stored in Rolodex
 * @param name
 * @param xStart
 * @param xEnd
 * @param yStart
 * @param yEnd
 * @param installationDate
 * @param wallNominalThickness
 * @param coatingNominalThickness
 */
export function generatePlate(
  name: string,
  xStart: GeckoMeasurement,
  xEnd: GeckoMeasurement,
  yStart: GeckoMeasurement,
  yEnd: GeckoMeasurement,
  installationDate: Date | undefined,
  wallNominalThickness: GeckoMeasurement | undefined,
  coatingNominalThickness: GeckoMeasurement | undefined
): GeckoPlateJSON {
  const plateJSON: GeckoPlateJSON = {
    type: GeckoFeatureModelType.Plate,
    name: name,
    feature_geometry: {
      x_start: xStart
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
      x_end: xEnd
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,

      y_start: yStart
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,

      y_end: yEnd
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
    },
    surface_specs: {
      wall_nominal_thickness: undefined,
      coating_nominal_thickness: undefined,
    },
    installation_date: undefined,
  }

  if (installationDate) {
    plateJSON.installation_date = installationDate.toISOString().split('T')[0]
  }

  if (wallNominalThickness) {
    plateJSON.surface_specs.wall_nominal_thickness = wallNominalThickness
      .convertTo(GeckoMeasurementUnit.Meters)
      .toPrecision(assetModelPrecision).num
  }

  if (coatingNominalThickness) {
    plateJSON.surface_specs.coating_nominal_thickness = coatingNominalThickness
      .convertTo(GeckoMeasurementUnit.Meters)
      .toPrecision(assetModelPrecision).num
  }

  return plateJSON
}

/**
 * Determine the thickness for a plate based on its location within a shell course
 * Function expects to consume the shell course data in the form found in the asset model (GeckoCourseJSON interface)
 * @param plateYEnd
 * @param shellCourses
 */
export function getNominalThicknessForPlateFromCourse(
  plateYEnd: GeckoMeasurement,
  shellCourses: Map<string, GeckoCourseJSON>
): Array<number | undefined> {
  const filteredCourses: Array<GeckoCourseJSON> = []
  shellCourses.forEach((course) => {
    const featureGeometry = course.feature_geometry as Record<string, number>
    if (
      featureGeometry.y_end <= plateYEnd.toNumber(GeckoMeasurementUnit.Meters)
    ) {
      filteredCourses.push(course)
    }
  })

  const sortedCourses = filteredCourses.sort((a, b) => {
    const featureGeometryA = a.feature_geometry as Record<string, number>
    const featureGeometryB = b.feature_geometry as Record<string, number>
    return featureGeometryB.y_end - featureGeometryA.y_end
  })

  if (sortedCourses.length === 0) {
    console.warn(
      `Unable to get thickness value for plate at y_end position ${plateYEnd.toNumber(GeckoMeasurementUnit.Meters)}. No shell course found for that bound!`
    )
    return [undefined, undefined]
  }

  return [
    sortedCourses[0].nominal.wall_thickness,
    sortedCourses[0].nominal.coating_thickness,
  ]
}

interface PlateCoordinates {
  xStart: number
  xEnd: number
  yStart: number
  yEnd: number
}

/**
 * Consume data in the GeckoVerticalWeldJSON format and generate plates from it.
 * @param verticalWelds
 * @param shellCourses
 * @param dimensionUnits
 * @param thicknessUnits
 * @param circumference
 */
export function generatePlatesFromVerticalWelds(
  verticalWelds: Array<GeckoVerticalWeldJSON>,
  shellCourses: Map<string, GeckoCourseJSON>,
  dimensionUnits: GeckoMeasurementUnit,
  thicknessUnits: GeckoMeasurementUnit,
  circumference: GeckoMeasurement
) {
  const organizedWelds = new Map<string, Array<GeckoVerticalWeldJSON>>()
  const plates = new Map<string, GeckoPlateJSON>()

  // Segment the welds by their y values, allowing plates to be numbered sequentially
  verticalWelds.forEach((weld) => {
    const weldYAxisStart = weld.feature_geometry.y_start

    if (!organizedWelds.has(String(weldYAxisStart))) {
      organizedWelds.set(
        String(weldYAxisStart),
        new Array<GeckoVerticalWeldJSON>()
      )
    }

    organizedWelds.get(String(weldYAxisStart))?.push(weld)
  })

  let plateNumber = 1
  // Iterate through the y start values
  organizedWelds.forEach((yAxisStart) => {
    // Sort the welds into ascending order based on the x value
    yAxisStart.sort((a, b) => {
      return a.feature_geometry.x <= b.feature_geometry.x ? -1 : 1
    })

    let previousWeld
    for (const [idx, weld] of yAxisStart.entries()) {
      const platesToAdd: PlateCoordinates[] = []

      if (idx === 0) {
        if (weld.feature_geometry.x === 0) {
          // If we're at the first weld, and the weld location is on the axis
          // Skip this weld, and move to the next one as the plate does not wrap
          // And we'll start generating on the next plate
          previousWeld = weld
          continue
        } else {
          // If we're at the first weld, and the weld location is not on the axis
          // Get the location of the final weld, as this plate wraps
          const lastWeld = yAxisStart[yAxisStart.length - 1]

          platesToAdd.push({
            xStart: lastWeld.feature_geometry.x,
            xEnd: weld.feature_geometry.x,
            yStart: weld.feature_geometry.y_start,
            yEnd: weld.feature_geometry.y_end,
          })
        }
      } else if (idx === yAxisStart.length - 1) {
        const firstWeld = yAxisStart[0]
        if (firstWeld.feature_geometry.x === 0) {
          // If we're at the last weld, and the first weld started at zero
          // Create an extra plate that ends at the circumference (since we skipped the axis weld above)
          if (!previousWeld) {
            throw Error('Previous weld undefined!')
          }

          // The original plate, based on the last weld
          platesToAdd.push({
            xStart: previousWeld.feature_geometry.x,
            xEnd: weld.feature_geometry.x,
            yStart: previousWeld.feature_geometry.y_start,
            yEnd: previousWeld.feature_geometry.y_end,
          })

          // The extra plate, that extends from the last weld to the zero axis
          platesToAdd.push({
            xStart: weld.feature_geometry.x,
            xEnd: circumference.toNumber(dimensionUnits),
            yStart: weld.feature_geometry.y_start,
            yEnd: weld.feature_geometry.y_end,
          })
        } else {
          // If we're at the last weld, but the first weld did not start at zero
          const lastWeld = yAxisStart[yAxisStart.length - 1]
          if (!previousWeld) {
            throw Error('Previous weld undefined!')
          }

          platesToAdd.push({
            xStart: previousWeld.feature_geometry.x,
            xEnd: lastWeld.feature_geometry.x,
            yStart: previousWeld.feature_geometry.y_start,
            yEnd: previousWeld.feature_geometry.y_end,
          })
        }
      } else {
        // Otherwise, we're somewhere in the middle
        // Get the starting x position from the previous weld, and the rest from this weld
        if (!previousWeld) {
          throw Error('Previous weld undefined!')
        }

        platesToAdd.push({
          xStart: previousWeld.feature_geometry.x,
          xEnd: weld.feature_geometry.x,
          yStart: weld.feature_geometry.y_start,
          yEnd: weld.feature_geometry.y_end,
        })
      }

      // For each plate coordinates created above (should always be 1 or 2),
      // generate a GeckoPlateJSON add each to the list of plates
      for (const plateToAdd of platesToAdd) {
        const xStartGM = GeckoMeasurement.CreateFromValue(
          plateToAdd.xStart,
          dimensionUnits
        )
        const xEndGM = GeckoMeasurement.CreateFromValue(
          plateToAdd.xEnd,
          dimensionUnits
        )
        const yStartGM = GeckoMeasurement.CreateFromValue(
          plateToAdd.yStart,
          dimensionUnits
        )
        const yEndGM = GeckoMeasurement.CreateFromValue(
          plateToAdd.yEnd,
          dimensionUnits
        )

        const plateID = uuid4().slice(0, 6)
        const plateName = `Plate ${plateNumber}`
        const [wall_thickness, coating_thickness] =
          getNominalThicknessForPlateFromCourse(yEndGM, shellCourses)

        const plate = generatePlate(
          plateName,
          xStartGM,
          xEndGM,
          yStartGM,
          yEndGM,
          undefined,
          wall_thickness
            ? GeckoMeasurement.CreateFromValue(wall_thickness, thicknessUnits)
            : undefined,
          coating_thickness
            ? GeckoMeasurement.CreateFromValue(
                coating_thickness,
                thicknessUnits
              )
            : undefined
        )
        plates.set(plateID, plate)
        plateNumber += 1
        previousWeld = weld
      }
    }
  })

  return plates
}
